import { useEffect, useRef, useState } from "react";
import { api, fmtErr } from "../../lib/api";
import { Button } from "../ui/button";
import { UploadSimple, Table, FileText, X } from "@phosphor-icons/react";
import { toast } from "sonner";

const PHASES = {
  queued: "Queued…",
  reading: "Reading the template (OCR for scans)…",
  rows: "Reading every parameter row…",
  pack: "Identifying the equipment / industry pack…",
  mapping: "Mapping rows onto the data model…",
};

/** Upload a specification TEMPLATE (xlsx / docx / pdf / csv / scan) and get the
 *  delta against the industry pack's data model. */
export default function SpecTemplateDrop({ onResult, disabled }) {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(null);
  const [drag, setDrag] = useState(false);
  const [saved, setSaved] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    api.get("/spec-templates").then(({ data }) => setSaved(data)).catch(() => {});
  }, []);

  const addFiles = (list) => setFiles([...files, ...Array.from(list)].slice(0, 8));

  const run = async () => {
    if (!files.length) return;
    setBusy(true);
    setStep({ phase: "queued" });
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      if (templateId) fd.append("template_id", templateId);
      const { data } = await api.post("/spec-templates/analyze", fd);
      for (;;) {
        await new Promise((r) => setTimeout(r, 2500));
        const { data: job } = await api.get(`/spec-templates/jobs/${data.job_id}`);
        setStep(job.step);
        if (job.status === "done") {
          onResult(job.result);
          const s = job.result.summary;
          toast.success(`Template read — ${s.mapped + s.converted} of ${s.rows} rows mapped to the data model, `
            + `${s.unmapped} with no field, ${s.uncovered} model fields not in the template`);
          setFiles([]);
          break;
        }
        if (job.status === "error") throw new Error(job.error || "Template analysis failed");
      }
    } catch (e) {
      toast.error(fmtErr(e), { duration: 8000 });
    } finally {
      setBusy(false);
      setStep(null);
    }
  };

  return (
    <div data-testid="spec-template-drop"
      className="relative overflow-hidden rounded-xl border border-[var(--c-border)] bg-[var(--c-surface)]">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[var(--c-green)] to-transparent opacity-70" />
      <div className="p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <Table size={15} className="text-[var(--c-green)]" />
              <span className="text-sm font-medium text-[var(--c-text)]">Read a specification template</span>
            </div>
            <p className="text-xs text-[var(--c-muted)] mt-1.5 max-w-xl leading-relaxed">
              Drop your own or the client's spec sheet in any format — Excel, Word, PDF, CSV or a scan.
              Cognara AI reads every printed row, maps it onto the data model and shows the delta: what
              lines up, what needs a unit conversion, what the data model has no field for and what the
              template leaves out.
            </p>
          </div>
          <Button data-testid="spec-template-run" onClick={run} disabled={busy || disabled || !files.length}
            className="bg-[var(--c-green)] hover:bg-[var(--c-green)]/85 text-white rounded-sm shrink-0">
            <Table size={14} className="mr-1.5" /> {busy ? "Reading…" : "Read template"}
          </Button>
        </div>

        {saved.length > 0 && (
          <div className="mt-3 flex items-center gap-2" data-testid="spec-template-saved">
            <span className="text-[10px] font-mono uppercase tracking-wider text-[var(--c-muted)]">Saved mapping</span>
            <select data-testid="spec-template-saved-select" value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              className="h-7 flex-1 bg-[var(--c-bg)] border border-[var(--c-border)] text-[var(--c-text)] text-[11px] rounded-sm px-2">
              <option value="">— map fresh with AI —</option>
              {saved.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}{t.client_name ? ` · ${t.client_name}` : ""} ({t.rows} rows)
                </option>
              ))}
            </select>
          </div>
        )}

        <div
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); }}
          onClick={() => inputRef.current?.click()}
          data-testid="spec-template-dropzone"
          className={`mt-4 cursor-pointer rounded-lg border border-dashed px-4 py-6 text-center transition-colors duration-200
            ${drag ? "border-[var(--c-green)] bg-[var(--c-green)]/5" : "border-[var(--c-border)] hover:border-[var(--c-green)]/60"}`}>
          <UploadSimple size={20} className="mx-auto text-[var(--c-muted)]" />
          <div className="text-xs text-[var(--c-text)] mt-2">Drop the template here or click to browse</div>
          <div className="text-[10px] text-[var(--c-muted)] mt-1 font-mono">xlsx · csv · docx · pdf · txt · png · jpg — up to 8 files, 30 MB each</div>
          <input ref={inputRef} type="file" multiple hidden data-testid="spec-template-file-input"
            onChange={(e) => addFiles(e.target.files)} />
        </div>

        {files.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2" data-testid="spec-template-file-list">
            {files.map((f, i) => (
              <span key={`${f.name}-${i}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-[var(--c-border)] bg-[var(--c-bg)] pl-2.5 pr-1.5 py-1 text-[11px] text-[var(--c-text)]">
                <FileText size={12} className="text-[var(--c-muted)]" /> {f.name}
                <button data-testid={`spec-template-remove-${i}`}
                  onClick={(e) => { e.stopPropagation(); setFiles(files.filter((_, j) => j !== i)); }}
                  className="rounded-full p-0.5 text-[var(--c-muted)] hover:text-[var(--c-red)] transition-colors duration-150">
                  <X size={11} />
                </button>
              </span>
            ))}
          </div>
        )}

        {busy && (
          <div className="mt-4" data-testid="spec-template-progress">
            <div className="flex items-center justify-between text-[10px] font-mono text-[var(--c-muted)] mb-1.5">
              <span>{PHASES[step?.phase] || "Working…"}</span>
              <span>{step?.detail || ""}</span>
            </div>
            <div className="h-1 overflow-hidden rounded-full bg-[var(--c-border)]">
              <div className="h-full w-1/3 animate-[slide_1.4s_ease-in-out_infinite] bg-[var(--c-green)]" />
            </div>
          </div>
        )}
      </div>
      <style>{`@keyframes slide{0%{transform:translateX(-100%)}100%{transform:translateX(300%)}}`}</style>
    </div>
  );
}
